/**
 * Push Notification Service
 * 
 * Handles web push notifications using Firebase Cloud Messaging
 * - Request permission
 * - Get/save FCM tokens
 * - Handle foreground messages
 */

import { supabase } from '../lib/supabase';
import {
    initializeFirebase,
    requestNotificationPermission,
    onForegroundMessage,
    getCurrentToken
} from '../lib/firebaseConfig';
import toast from 'react-hot-toast';

/**
 * Check if push notifications are supported in this browser
 * @returns {boolean}
 */
export const isPushSupported = () => {
    return 'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window;
};

/**
 * Check if the app is running in standalone mode (installed PWA)
 * @returns {boolean}
 */
export const isStandalone = () => {
    return window.matchMedia('(display-mode: standalone)').matches ||
        window.navigator.standalone === true ||
        document.referrer.includes('android-app://');
};

/**
 * Get current notification permission status
 * @returns {'granted' | 'denied' | 'default'}
 */
export const getPermissionStatus = () => {
    if (!isPushSupported()) return 'denied';
    return Notification.permission;
};

/**
 * Initialize push notifications and save token to database
 * @param {string} userId - The user's ID
 * @returns {Promise<{success: boolean, token?: string, error?: string}>}
 */
export const initializePushNotifications = async (userId) => {
    try {
        // Check browser support
        if (!isPushSupported()) {
            console.log('Push notifications not supported in this browser');
            return { success: false, error: 'Not supported' };
        }

        // Initialize Firebase
        const initialized = await initializeFirebase();
        if (!initialized) {
            return { success: false, error: 'Firebase initialization failed' };
        }

        // Set up foreground message handler
        onForegroundMessage((payload) => {
            handleForegroundMessage(payload);
        });

        // Check if we already have permission
        if (Notification.permission === 'granted') {
            const token = await getCurrentToken();
            if (token) {
                await saveTokenToDatabase(userId, token);
                return { success: true, token };
            }
        }

        return { success: true };
    } catch (error) {
        console.error('Error initializing push notifications:', error);
        return { success: false, error: error.message };
    }
};

/**
 * Request permission and get FCM token
 * @param {string} userId - The user's ID
 * @returns {Promise<{success: boolean, token?: string, error?: string}>}
 */
export const requestPushPermission = async (userId) => {
    try {
        if (!isPushSupported()) {
            return { success: false, error: 'Push notifications not supported' };
        }

        // Request permission and get token
        const token = await requestNotificationPermission();

        if (!token) {
            return { success: false, error: 'Permission denied or token unavailable' };
        }

        // Save token to database
        await saveTokenToDatabase(userId, token);

        toast.success('Push notifications enabled!');
        return { success: true, token };
    } catch (error) {
        console.error('Error requesting push permission:', error);
        return { success: false, error: error.message };
    }
};

/**
 * Save FCM token to Supabase database
 * @param {string} userId - The user's ID
 * @param {string} token - The FCM token
 */
const saveTokenToDatabase = async (userId, token) => {
    try {
        // Get device info
        const deviceInfo = {
            userAgent: navigator.userAgent,
            platform: navigator.platform,
            language: navigator.language,
            screenWidth: window.screen.width,
            screenHeight: window.screen.height,
        };

        // Upsert token (insert or update if exists)
        const { error } = await supabase
            .from('push_tokens')
            .upsert({
                userId,
                token,
                platform: 'web',
                deviceInfo,
                updatedAt: new Date().toISOString(),
            }, {
                onConflict: 'userId,token',
                ignoreDuplicates: false,
            });

        if (error) {
            console.error('Error saving push token:', error);
            throw error;
        }

        console.log('Push token saved successfully');
    } catch (error) {
        console.error('Error saving token to database:', error);
        throw error;
    }
};

/**
 * Remove FCM token from database (e.g., on logout)
 * @param {string} userId - The user's ID
 */
export const removeTokenFromDatabase = async (userId) => {
    try {
        const token = await getCurrentToken();
        if (token) {
            const { error } = await supabase
                .from('push_tokens')
                .delete()
                .eq('userId', userId)
                .eq('token', token);

            if (error) {
                console.error('Error removing push token:', error);
            }
        }
    } catch (error) {
        console.error('Error removing token:', error);
    }
};

/**
 * Handle foreground notifications (when app is open)
 * @param {object} payload - FCM message payload
 */
const handleForegroundMessage = (payload) => {
    console.log('Foreground notification received:', payload);

    const title = payload.notification?.title || payload.data?.title || 'GK4I';
    const body = payload.notification?.body || payload.data?.body || '';
    const data = payload.data || {};

    // Show toast notification
    toast.custom((t) => (
        <div
            className={`${t.visible ? 'animate-enter' : 'animate-leave'
                } max-w-md w-full bg-white shadow-lg rounded-lg pointer-events-auto flex ring-1 ring-black ring-opacity-5`}
            onClick={() => {
                toast.dismiss(t.id);
                if (data.url) {
                    window.location.href = data.url;
                }
            }}
        >
            <div className="flex-1 w-0 p-4">
                <div className="flex items-start">
                    <div className="flex-shrink-0 pt-0.5">
                        <img
                            className="h-10 w-10 rounded-full"
                            src="/logo192.png"
                            alt="SayDo"
                        />
                    </div>
                    <div className="ml-3 flex-1">
                        <p className="text-sm font-medium text-gray-900">{title}</p>
                        <p className="mt-1 text-sm text-gray-500">{body}</p>
                    </div>
                </div>
            </div>
            <div className="flex border-l border-gray-200">
                <button
                    onClick={() => toast.dismiss(t.id)}
                    className="w-full border border-transparent rounded-none rounded-r-lg p-4 flex items-center justify-center text-sm font-medium text-indigo-600 hover:text-indigo-500"
                >
                    Close
                </button>
            </div>
        </div>
    ), {
        duration: 5000,
        position: 'top-right',
    });

    // Also show browser notification if desired
    if (data.showBrowserNotification === 'true') {
        new Notification(title, {
            body,
            icon: '/logo192.png',
            badge: '/favicon-32x32.png',
        });
    }
};

/**
 * Check if user has enabled push notifications
 * @param {string} userId - The user's ID
 * @returns {Promise<boolean>}
 */
export const hasEnabledPush = async (userId) => {
    try {
        const { data, error } = await supabase
            .from('push_tokens')
            .select('id')
            .eq('userId', userId)
            .eq('platform', 'web')
            .limit(1);

        if (error) throw error;
        return data && data.length > 0;
    } catch (error) {
        console.error('Error checking push status:', error);
        return false;
    }
};
